import { useState, useEffect, useRef } from "react";
import SvgMapper from "../../features/common/SvgMapper";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleBulkScheduleModalOpen,
  setValidationErrors,
  clearValidationErrors,
  resetScheduleForm,
  setScheduleCreated,
  setIsBulkSchedule,
} from "../../../store/schedulingReducer";
import SelectInteview from "./sections/SelectInterview";
import { addBulkStudent } from "../../../application/services/studentManagementService";
import "../Style.css";

const BulkScheduleModal = () => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [csvValidationError, setCsvValidationError] = useState("");
  const { validationErrors, isBulkScheduleModalOpen, interviewTypeId } = useSelector(
    (state) => state.scheduling
  );
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isBulkScheduleModalOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isBulkScheduleModalOpen]);

  const onclose = () => {
    dispatch(clearValidationErrors());
    dispatch(resetScheduleForm());
    dispatch(toggleBulkScheduleModalOpen(false));
    setSelectedFile(null);
    setFileError("");
    setCsvValidationError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateDateFormat = (dateString) => {
    if (!dateString || !dateString.trim()) {
      return false;
    }
    const trimmedDate = dateString.trim();
    const format1Regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/; // MM/DD/YYYY HH:mm:ss
    const format2Regex = /^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{2}):(\d{2})$/; // YYYY-MM-DD HH:mm:ss
    
    if (!format1Regex.test(trimmedDate) && !format2Regex.test(trimmedDate)) {
      return false;
    }
    const date = new Date(trimmedDate);
    return !isNaN(date.getTime());
  };

  const validateCSVColumns = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split("\n").filter((line) => line.trim() !== "");
          
          if (lines.length === 0) {
            reject("CSV file is empty");
            return;
          }
          const headerLine = lines[0];
          const headers = headerLine
            .split(",")
            .map((h) => h.trim().toLowerCase().replace(/"/g, ""));

          const hasStudentEmail = headers.some(
            (h) => h === "student_email"
          );
          const hasActiveFrom = headers.some(
            (h) => h === "active_from"
          );
          const hasActiveTo = headers.some(
            (h) => h === "active_to"
          );

          const missingColumns = [];
          if (!hasStudentEmail) {
            missingColumns.push("student_email");
          }
          if (!hasActiveFrom) {
            missingColumns.push("active_from");
          }
          if (!hasActiveTo) {
            missingColumns.push("active_to");
          }

          if (missingColumns.length > 0) {
            reject(
              `CSV is missing required columns: ${missingColumns.join(", ")}. Required columns: student_email, active_from, active_to`
            );
            return;
          }

          if (lines.length < 2) {
            reject("CSV file must contain at least one data row");
            return;
          }
          const studentEmailIndex = headers.indexOf("student_email");
          const activeFromIndex = headers.indexOf("active_from");
          const activeToIndex = headers.indexOf("active_to");

          const dateErrors = [];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i];
            const values = [];
            let currentValue = "";
            let inQuotes = false;
            
            for (let j = 0; j < line.length; j++) {
              const char = line[j];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(currentValue.trim().replace(/^"|"$/g, ""));
                currentValue = "";
              } else {
                currentValue += char;
              }
            }
            values.push(currentValue.trim().replace(/^"|"$/g, ""));
            
            if (values.length <= Math.max(studentEmailIndex, activeFromIndex, activeToIndex)) {
              dateErrors.push(`Row ${i + 1}: Insufficient columns`);
              continue;
            }

            const activeFrom = values[activeFromIndex];
            const activeTo = values[activeToIndex];

            if (!activeFrom || !validateDateFormat(activeFrom)) {
              dateErrors.push(
                `Row ${i + 1}: active_from must be in format MM/DD/YYYY HH:mm:ss (e.g., 12/21/2025 13:38:00) or YYYY-MM-DD HH:mm:ss (e.g., 2025-12-21 13:38:00). Found: "${activeFrom || 'empty'}"`
              );
            }

            if (!activeTo || !validateDateFormat(activeTo)) {
              dateErrors.push(
                `Row ${i + 1}: active_to must be in format MM/DD/YYYY HH:mm:ss (e.g., 12/21/2025 13:38:00) or YYYY-MM-DD HH:mm:ss (e.g., 2025-12-21 13:38:00). Found: "${activeTo || 'empty'}"`
              );
            }
            if (activeFrom && activeTo && validateDateFormat(activeFrom) && validateDateFormat(activeTo)) {
              const fromDate = new Date(activeFrom);
              const toDate = new Date(activeTo);
              if (fromDate >= toDate) {
                dateErrors.push(
                  `Row ${i + 1}: active_from must be before active_to`
                );
              }
            }
          }

          if (dateErrors.length > 0) {
            reject(`Date validation errors:\n${dateErrors.join("\n")}`);
            return;
          }

          resolve({ headers, isValid: true });
        } catch (error) {
          reject(`Error reading CSV file: ${error.message}`);
        }
      };

      reader.onerror = () => {
        reject("Error reading file");
      };

      reader.readAsText(file);
    });
  };

  const uploadCSVToS3 = async (file) => {
    try {
      const formData = new FormData();
      formData.append("folder", "schedule-bulk-uploads");
      formData.append("file", file);

      const timestamp = Date.now();
      const response = await fetch(
        `https://testservice.kraftshala.com/storage/add-external?timestamp=${timestamp}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("File upload failed");
      const data = await response.json();
      return data?.url || data?.fileUrl || null;
    } catch (error) {
      console.error("Error uploading CSV to S3:", error);
      return null;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    setFileError("");
    setCsvValidationError("");
    setSelectedFile(null);
    dispatch(clearValidationErrors());
    
    if (!file) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setFileError("Please upload a CSV file");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    try {
      await validateCSVColumns(file);
      setSelectedFile(file);
      setCsvValidationError("");
      setFileError("");
    } catch (error) {
      const errorMessage = error.message || error.toString() || "Invalid CSV file format";
      setCsvValidationError(errorMessage);
      setSelectedFile(null);
      setFileError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const onBulkSchedule = async (e) => {
    e.preventDefault();
    try {
      dispatch(clearValidationErrors());
      setFileError("");

      const errors = {};
      if (!interviewTypeId) {
        errors.interviewTypeId = "Interview Type is required";
      }
      if (!selectedFile) {
        errors.file = "CSV file is required";
      }
      if (csvValidationError) {
        errors.csv = csvValidationError;
      }

      if (Object.keys(errors).length > 0) {
        dispatch(setValidationErrors(errors));
        if (errors.file) {
          setFileError(errors.file);
        }
        return;
      }

      try {
        await validateCSVColumns(selectedFile);
      } catch (error) {
        const errorMessage = error.message || error.toString() || "Invalid CSV file format";
        setCsvValidationError(errorMessage);
        dispatch(setValidationErrors({ csv: errorMessage }));
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(true);

      const s3Url = await uploadCSVToS3(selectedFile);

      if (!s3Url) {
        throw new Error("Failed to upload CSV file to S3");
      }

      const payload = {
        additional_param_name: "interview_config_id",
        additional_param_value: interviewTypeId,
        document: s3Url,
        request_type: "BULK_SCHEDULE_INTERVIEW"
      };

      const response = await addBulkStudent(payload);
      const result = response?.data || response;

      if (result) {
        dispatch(setIsBulkSchedule(true));
        dispatch(setScheduleCreated(true));
        dispatch(toggleBulkScheduleModalOpen(false));
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("Error bulk scheduling:", error);
      const errorMsg =
        error?.message || error?.data?.message || "Failed to bulk schedule. Please try again.";
      dispatch(setValidationErrors({ general: errorMsg }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasErrors = Object.keys(validationErrors).length > 0;

  return (
    <div className="space-y-6 font-sans">
      <div className="modal-overlay p-4">
        <div className="modal-container">
          <div className="modal-header">
            <h2 className="text-xl font-bold text-gray-900">Bulk Schedule Interview</h2>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={onclose}
              disabled={isSubmitting}
            >
              {SvgMapper.closeIcon("w-6 h-6")}
            </button>
          </div>
          <form className="p-6 space-y-8" onSubmit={onBulkSchedule}>
            {hasErrors && (
              <div className="error-box">
                <ul className="error-list">
                  {Object.entries(validationErrors || {}).map(([field, error]) => (
                    <li key={field}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-6">
              <SelectInteview />

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Upload CSV File
                </label>
                <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-semibold text-blue-900 mb-1">
                    Required CSV Columns:
                  </p>
                  <p className="text-xs text-blue-800">
                    The CSV file must include the following columns (case-insensitive):
                  </p>
                  <ul className="text-xs text-blue-700 mt-1 list-disc list-inside space-y-0.5">
                    <li><strong>student_email</strong> (required)</li>
                    <li><strong>active_from</strong> (required) - Format: MM/DD/YYYY HH:mm:ss (e.g., 12/21/2025 13:38:00) or YYYY-MM-DD HH:mm:ss (e.g., 2025-12-21 13:38:00)</li>
                    <li><strong>active_to</strong> (required) - Format: MM/DD/YYYY HH:mm:ss (e.g., 12/21/2025 13:38:00) or YYYY-MM-DD HH:mm:ss (e.g., 2025-12-21 13:38:00)</li>
                  </ul>
                  <p className="text-xs text-blue-700 mt-2">
                    <strong>Note:</strong> active_from must be before active_to
                  </p>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csv-upload"
                  disabled={isSubmitting}
                />
                <label
                  htmlFor="csv-upload"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="text-gray-700">
                    {selectedFile ? selectedFile.name : "Choose CSV file..."}
                  </span>
                  {SvgMapper.fileIcon("w-5 h-5 text-gray-400")}
                </label>
                {fileError && (
                  <p className="mt-1 text-sm text-red-600">{fileError}</p>
                )}
                {csvValidationError && (
                  <div className="mt-1 text-sm text-red-600 whitespace-pre-line">
                    {csvValidationError}
                  </div>
                )}
                {selectedFile && !csvValidationError && (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ File selected: {selectedFile.name} (Valid CSV format)
                  </p>
                )}
              </div>
            </div>

            <div className="pt-4 pb-6 px-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onclose}
                className="cancel-btn"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !interviewTypeId || !selectedFile}
                className={`submit-btn ${
                  isSubmitting || !interviewTypeId || !selectedFile ? "disabled" : ""
                }`}
              >
                {SvgMapper.saveIcon("w-4 h-4")}
                <span>{isSubmitting ? "Uploading..." : "Upload & Schedule"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkScheduleModal;

